import CloseIcon from '@mui/icons-material/Close';
import { Alert, Button, IconButton } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  refreshEmailVerification,
  sendVerificationEmail,
  toAuthUser,
} from '../../firebase/auth.utils';
import useAuthErrorSnackbar from '../../hooks/useAuthErrorSnackbar';
import { useAppDispatch } from '../../redux/hooks';
import { setCurrentUser } from '../../redux/user/user.slice';
import './EmailVerificationBanner.scss';

const RESEND_COOLDOWN_MS = 60_000;
// Re-checking reloads the Auth user (a network call), so don't do it on every tab switch.
const VERIFICATION_CHECK_INTERVAL_MS = 30_000;
const DISMISSED_STORAGE_KEY = 'wander:email-verification-banner-dismissed';

// Dismissal is a per-tab convenience; storage can be unavailable (private mode), so never throw.
const readDismissed = (): boolean => {
  try {
    return sessionStorage.getItem(DISMISSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const writeDismissed = () => {
  try {
    sessionStorage.setItem(DISMISSED_STORAGE_KEY, 'true');
  } catch {
    // Banner just reappears on next load.
  }
};

interface EmailVerificationBannerProps {
  email: string | null;
}

/**
 * Prompts email/password users to verify their email. Render only for unverified users:
 * while mounted it re-checks verification on mount and whenever the window regains focus
 * (e.g. after clicking the link in another tab).
 */
function EmailVerificationBanner({ email }: EmailVerificationBannerProps) {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const showAuthError = useAuthErrorSnackbar();
  const [isDismissed, setIsDismissed] = useState(readDismissed);
  const [isSending, setIsSending] = useState(false);
  const [isCoolingDown, setIsCoolingDown] = useState(false);
  const cooldownTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const lastCheckRef = useRef(0);

  const checkVerification = useCallback(async () => {
    if (Date.now() - lastCheckRef.current < VERIFICATION_CHECK_INTERVAL_MS) {
      return;
    }
    lastCheckRef.current = Date.now();
    const verifiedUser = await refreshEmailVerification().catch(() => null);
    if (verifiedUser) {
      dispatch(setCurrentUser(toAuthUser(verifiedUser))); // Unmounts this banner.
      enqueueSnackbar('Email verified', { variant: 'success' });
    }
  }, [dispatch, enqueueSnackbar]);

  useEffect(() => {
    // A cached session can be stale if the user verified in another tab before this load.
    checkVerification();
    window.addEventListener('focus', checkVerification);
    return () => window.removeEventListener('focus', checkVerification);
  }, [checkVerification]);

  useEffect(() => () => clearTimeout(cooldownTimeoutRef.current), []);

  const handleResend = async () => {
    setIsSending(true);
    try {
      await sendVerificationEmail();
      enqueueSnackbar(`Verification email sent to ${email}`, { variant: 'success' });
      setIsCoolingDown(true);
      cooldownTimeoutRef.current = setTimeout(() => setIsCoolingDown(false), RESEND_COOLDOWN_MS);
    } catch (error) {
      showAuthError(error, 'Could not send the verification email.');
    } finally {
      setIsSending(false);
    }
  };

  const handleDismiss = () => {
    writeDismissed();
    setIsDismissed(true);
  };

  if (isDismissed) {
    return null;
  }

  return (
    <Alert
      className='email-verification-banner'
      severity='info'
      action={
        // A custom action replaces Alert's built-in close button, so render both here.
        <>
          <Button
            color='inherit'
            size='small'
            onClick={handleResend}
            disabled={isSending || isCoolingDown}
          >
            {isCoolingDown ? 'Sent' : 'Resend email'}
          </Button>
          <IconButton
            color='inherit'
            size='small'
            aria-label='Dismiss'
            onClick={handleDismiss}
          >
            <CloseIcon fontSize='small' />
          </IconButton>
        </>
      }
    >
      Verify your email to keep your account secure. We sent a link to {email}.
    </Alert>
  );
}

EmailVerificationBanner.displayName = 'EmailVerificationBanner';
export default EmailVerificationBanner;
